import moment from 'moment';

/**
 * ACTION PROFILE EDIT INIT
 * Reset edit profile
 */
export const profileEditInit = () => {
  return (dispatch) => {
    dispatch({ type: 'PROFILE_EDIT_INIT' });
  };
};

/**
 * ACTION PROFILE EDIT
 * Edit Profile data
 * @param {object} data - profile data
 */
export const profileEdit = (data) => {
  return async (dispatch, getState, { getFirebase }) => {
    const profileCompany = getState().firebase.profile.company;
    if (data !== undefined && data.fullname !== undefined) {
      dispatch({ type: 'PROFILE_EDIT_PENDING' });
      const firebase = getFirebase();
      const firestore = getFirebase().firestore();
      const currentUser = getFirebase().auth().currentUser;

      const profileEditRole = async (role, id) => {
        const profileRef = await firestore
          .collection('users')
          .doc(currentUser.uid);
        return await firestore
          .collection('companies')
          .doc(profileCompany.id)
          .update(
            role === 'admins'
              ? {
                  admins: firebase.firestore.FieldValue.arrayUnion(profileRef),
                  users: firebase.firestore.FieldValue.arrayRemove(profileRef),
                }
              : role === 'users'
              ? {
                  admins: firebase.firestore.FieldValue.arrayRemove(profileRef),
                  users: firebase.firestore.FieldValue.arrayUnion(profileRef),
                }
              : {}
          )
          .then(() => {
            return true;
          })
          .catch((err) => {
            return err;
          });
      };

      const profileEditPhoto = async (photo) => {
        const photoStorage = firebase
          .storage()
          .ref(`users/${currentUser.uid}/photo.jpg`);
        return await photoStorage
          .put(photo)
          .then(async (snapshot) => {
            await snapshot.ref
              .getDownloadURL()
              .then(async (url) => {
                return await firestore
                  .collection('users')
                  .doc(currentUser.uid)
                  .update({
                    avatar: url,
                    updatedAt: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                  })
                  .then(() =>
                    dispatch({ type: 'PROFILE_PHOTO_UPDATE_SUCCESS' })
                  )
                  .catch((err) => {
                    throw err;
                  });
              })
              .catch((err) => {
                return err;
              });
          })
          .catch((err) => {
            return err;
          });
      };

      return await firestore
        .collection('users')
        .doc(currentUser.uid)
        .update({
          full_name: data.fullname,
          address: data.address || '',
          city: data.city || '',
          zip: data.zipCode || '',
          updatedAt: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
        })
        .then(async () => {
          if (profileCompany && data.role) await profileEditRole(data.role);
          if (data.photoData) await profileEditPhoto(data.photoData);

          dispatch({ type: 'PROFILE_EDIT_SUCCESS' });
          return true;
        })
        .catch(async (err) => {
          if (err.code === 'not-found') {
            return await firestore
              .collection('users')
              .doc(currentUser.uid)
              .set({
                full_name: data.fullname,
                address: data.address || '',
                city: data.city || '',
                zip: data.zipCode || '',
                createdAt: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                updatedAt: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
              })
              .then(async () => {
                if (data.company && data.role)
                  await profileEditRole(
                    data.company,
                    data.role,
                    currentUser.uid
                  );
                if (data.photoData)
                  await profileEditPhoto(currentUser.uid, data.photoData);

                dispatch({ type: 'PROFILE_EDIT_SUCCESS' });
                return true;
              })
              .catch((err) => {
                dispatch({ type: 'PROFILE_EDIT_ERROR', err });
                return false;
              });
          }
          dispatch({ type: 'PROFILE_EDIT_ERROR', err });
          return false;
        });
    } else return false;
  };
};

/**
 * ACTION PROFILE COMPANY EDIT INIT
 * Reset edit profile company
 */
export const profileCompanyEditInit = () => {
  return (dispatch) => {
    dispatch({ type: 'PROFILE_COMPANY_EDIT_INIT' });
  };
};

/**
 * ACTION PROFILE COMPANY EDIT
 * Edit Profile company data
 * @param {object} data - profile company data
 */
export const profileCompanyEdit = (data) => {
  return async (dispatch, getState, { getFirebase }) => {
    const profileCompany = getState().firebase.profile.company;
    if (
      profileCompany !== undefined &&
      data !== undefined &&
      data.company !== undefined &&
      data.accountStatus !== undefined &&
      data.invoiceAddress !== undefined &&
      data.invoiceCity !== undefined &&
      data.invoiceZipCode !== undefined &&
      data.invoiceCountry !== undefined &&
      data.invoiceTax !== undefined
    ) {
      dispatch({ type: 'PROFILE_COMPANY_EDIT_PENDING' });
      const firestore = getFirebase().firestore();

      return await firestore
        .collection('companies')
        .doc(profileCompany.id)
        .update({
          name: data.company,
          status: data.accountStatus,
          address: data.invoiceAddress,
          city: data.invoiceCity,
          zip: data.invoiceZipCode,
          country: data.invoiceCountry,
          tax: data.invoiceTax,
          updatedAt: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
        })
        .then(async () => {
          dispatch({ type: 'PROFILE_COMPANY_EDIT_SUCCESS' });
          return true;
        })
        .catch(async (err) => {
          dispatch({ type: 'PROFILE_COMPANY_EDIT_ERROR', err });
          return false;
        });
    } else return false;
  };
};

/**
 * ACTION PROFILE COMPANY BILLING EDIT INIT
 * Reset edit profile company billing
 */
export const profileCompanyBillingEditInit = () => {
  return (dispatch) => {
    dispatch({ type: 'PROFILE_COMPANY_BILLING_EDIT_INIT' });
  };
};

/**
 * ACTION PROFILE COMPANY BILLING EDIT
 * Edit Profile company billing data
 * @param {object} data - profile company billing data
 */
export const profileCompanyBillingEdit = (data) => {
  return async (dispatch, getState, { getFirebase }) => {
    const profileCompany = getState().firebase.profile.company;
    if (
      profileCompany !== undefined &&
      data !== undefined &&
      data.cardNumber !== undefined &&
      data.expiryDate !== undefined
    ) {
      dispatch({ type: 'PROFILE_COMPANY_BILLING_EDIT_PENDING' });
      const firestore = getFirebase().firestore();

      return await firestore
        .collection('companies')
        .doc(profileCompany.id)
        .collection('billings')
        .add({
          number: data.cardNumber,
          expiryAt: moment(data.expiryDate, 'MM/YYYY', true)
            .startOf('day')
            .format('YYYY-MM-DDTHH:mm:ssZ'),
          createdAt: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
          updatedAt: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
        })
        .then(async () => {
          dispatch({ type: 'PROFILE_COMPANY_BILLING_EDIT_SUCCESS' });
          return true;
        })
        .catch(async (err) => {
          dispatch({ type: 'PROFILE_COMPANY_BILLING_EDIT_ERROR', err });
          return false;
        });
    } else return false;
  };
};

/*
 * ACTION REGISTER PROFILE ACTIVITY
 * Register profile activity
 */
export const registerProfileActivity = () => {
  return async (dispatch, getState, { getFirebase }) => {
    const firestore = getFirebase().firestore();
    const currentUser = getFirebase().auth().currentUser;

    await firestore
      .collection('users')
      .doc(currentUser.uid)
      .update({
        loggedIn: moment().utc().format('YYYY-MM-DDTHH:mm:ssZ'),
      });
  };
};
